/**
 * Transaction Label Mapper — Converts raw internal keys to friendly EN/AR labels
 *
 * Used by WalletHistoryPremium + Wallet transaction mapping.
 * Never expose raw internal codes to users.
 */

const LABELS = {
  // ── Earning types ──
  earn_cashback:          { en: 'Coins Back',               ar: 'استرداد عملات' },
  earn_purchased:         { en: 'Coins Purchased',          ar: 'شراء عملات' },
  earn_daily_checkin:     { en: 'Daily Check-in',           ar: 'تسجيل الدخول اليومي' },
  earn_profile_complete:  { en: 'Profile Completed',        ar: 'إكمال الملف الشخصي' },
  earn_referral:          { en: 'Referral Reward',          ar: 'مكافأة الإحالة' },
  earn_post_reward:       { en: 'Ad Posted Reward',         ar: 'مكافأة نشر إعلان' },
  earn_first_post:        { en: 'First Ad Reward',          ar: 'مكافأة أول إعلان' },
  earn_streak_bonus:      { en: 'Login Streak Bonus',       ar: 'مكافأة سلسلة الدخول' },
  earn_volume_milestone:  { en: 'Sales Milestone',          ar: 'إنجاز مبيعات' },
  earn_fast_responder:    { en: 'Fast Responder Bonus',     ar: 'مكافأة الرد السريع' },
  earn_share_reward:      { en: 'Sharing Reward',           ar: 'مكافأة المشاركة' },
  earn_admin_grant:       { en: 'Admin Bonus',              ar: 'مكافأة إدارية' },

  // ── Spending types ──
  spend_boost:            { en: 'Boost Purchase',           ar: 'شراء تعزيز' },
  spend_cv_unlock:        { en: 'CV Builder Unlock',        ar: 'فتح منشئ السيرة' },
  spend_subscription:     { en: 'Subscription Payment',     ar: 'اشتراك' },
  spend_feature:          { en: 'Feature Purchase',         ar: 'شراء ميزة' },

  // ── Boost codes (map to friendly names) ──
  boost_BOOST_HIGHLIGHT_24H:     { en: 'Highlight Boost (24h)',       ar: 'تعزيز تمييز (24 ساعة)' },
  boost_AUTO_REFRESH_24H:        { en: 'Auto Refresh (24h)',          ar: 'تحديث تلقائي (24 ساعة)' },
  boost_BADGE_URGENT_7D:         { en: 'Urgent Badge (7 days)',       ar: 'شارة عاجل (7 أيام)' },
  boost_BOOST_TOP_CATEGORY_24H:  { en: 'Top Category (24h)',          ar: 'أعلى القسم (24 ساعة)' },
  boost_BOOST_HOMEPAGE_24H:      { en: 'Homepage Feature (24h)',      ar: 'ظهور الصفحة الرئيسية (24 ساعة)' },
  boost_AI_REWRITE_DESC:         { en: 'AI Description Rewrite',      ar: 'إعادة كتابة بالذكاء الاصطناعي' },

  // ── Legacy/fallback ──
  profile_complete_reward:  { en: 'Profile Completed',      ar: 'إكمال الملف الشخصي' },
  daily_checkin_reward:     { en: 'Daily Check-in',         ar: 'تسجيل الدخول اليومي' },
  referral_reward:          { en: 'Referral Reward',        ar: 'مكافأة الإحالة' },
  cashback_reward:          { en: 'Coins Back',             ar: 'استرداد عملات' },
  purchased:                { en: 'Coins Purchased',        ar: 'شراء عملات' },
  post_reward:              { en: 'Ad Posted Reward',       ar: 'مكافأة نشر إعلان' },
};

/**
 * Get a friendly label for a transaction type or code
 * @param {string} code - raw internal type/code
 * @param {string} lang - 'en' | 'ar'
 * @returns {string} friendly label
 */
export function getTransactionLabel(code, lang = 'en') {
  if (!code) return lang.startsWith('ar') ? 'معاملة' : 'Transaction';

  const isAr = lang.startsWith('ar');

  // Direct match
  const entry = LABELS[code];
  if (entry) return isAr ? entry.ar : entry.en;

  // Try with prefix variants
  const withBoost = `boost_${code}`;
  const boostEntry = LABELS[withBoost];
  if (boostEntry) return isAr ? boostEntry.ar : boostEntry.en;

  const withEarn = `earn_${code}`;
  const earnEntry = LABELS[withEarn];
  if (earnEntry) return isAr ? earnEntry.ar : earnEntry.en;

  // Prettify: replace underscores, title-case
  const pretty = code
    .replace(/^(earn_|spend_|boost_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  return pretty;
}

/**
 * Get subtitle text for a transaction
 * @param {object} txn - transaction object from backend
 * @param {string} lang - 'en' | 'ar'
 */
export function getTransactionSubtitle(txn, lang = 'en') {
  const isAr = lang.startsWith('ar');

  // Use backend description if available
  if (isAr && txn.description_ar) return txn.description_ar;
  if (!isAr && txn.description_en) return txn.description_en;

  // For earn types
  const type = txn.type || '';
  if (type.startsWith('earn_')) {
    return isAr ? 'مكافأة' : 'Reward';
  }
  if (type.startsWith('spend_')) {
    return isAr ? 'استخدام' : 'Usage';
  }

  return '';
}

/**
 * Get icon for a transaction type
 */
export function getTransactionIcon(txn) {
  const type = txn.type || '';
  if (type.includes('cashback')) return '💰';
  if (type.includes('purchased')) return '🛒';
  if (type.includes('checkin')) return '📅';
  if (type.includes('profile')) return '👤';
  if (type.includes('referral')) return '👥';
  if (type.includes('post') || type.includes('first_post')) return '📝';
  if (type.includes('streak')) return '🔥';
  if (type.includes('volume') || type.includes('milestone')) return '🏆';
  if (type.includes('fast')) return '⚡';
  if (type.includes('share')) return '📤';
  if (type.includes('boost')) return '🚀';
  if (type.includes('cv')) return '📄';
  if (type.includes('subscription')) return '👑';
  if (type.includes('admin')) return '🛡️';

  // Direction-based fallback
  const dir = txn.direction;
  if (dir === '+') return '↑';
  if (dir === '-') return '↓';
  return '•';
}
